import React, { lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";

import AuthLayout from "components/common/Layout/AuthLayout";
import {
  adminToolLinks,
  hostToolLinks,
  publicLinks,
  technichianToolLinks,
} from "constants/AppLinks";
import { useAppSelector } from "core/helpers";
import { useAuth } from "hooks/core";
import { UserPermissions } from "core/types/enums/PermissionsEnum";
import { WORK_ORDER_VIEW_PERMISSIONS } from "constants/permissions";
import RoleManagementScreen from "screens/private/host/roles/RoleManagementScreen";
import ProtectedRoute from "./ProtectedRoute";
import TenantsManagementScreen from "../screens/private/host/tenants/TenantsManagementScreen";
import TenantDetailsScreen from "../screens/private/host/tenants/TenantDetailsScreen";
import FeaturesManagementScreen from "../screens/private/host/features/FeaturesManagementScreen";
import FeatureDetailsScreen from "../screens/private/host/features/FeatureDetailsScreen";
import AdminManagementScreen from "../screens/private/host/admins/AdminManagementScreen";

// Private Screens
// Admin Tool
const UsersManagementScreen = lazy(
  () => import("screens/private/admin-tool/UsersManagementScreen"),
);

const FeaturesScreen = lazy(() => import("screens/private/admin-tool/features/FeaturesScreen"));

const CompanyListScreen = lazy(
  () => import("screens/private/admin-tool/company/CompanyListScreen"),
);
const CompanyDetailsScreen = lazy(
  () => import("screens/private/admin-tool/company/CompanyDetailsScreen"),
);
const MapScreen = lazy(() => import("screens/private/admin-tool/MapScreen"));

const VehiclesListScreen = lazy(
  () => import("screens/private/admin-tool/vehicles/VehiclesListScreen"),
);
const VehiclesAddEditScreen = lazy(
  () => import("screens/private/admin-tool/vehicles/VehiclesAddEditScreen"),
);

const ContactsAddEditScreen = lazy(
  () => import("screens/private/admin-tool/company/contacts/ContactsAddEditScreen"),
);
const CompanyEquipmentAddEditScreen = lazy(
  () => import("screens/private/admin-tool/company/equipment/CompanyEquipmentAddEditScreen"),
);
const CompanyEquipmentEditTabScreen = lazy(
  () => import("screens/private/admin-tool/company/equipment/CompanyEquipmentEditTabScreen"),
);
const NotesAddEditScreen = lazy(
  () => import("screens/private/admin-tool/notes/NotesAddEditScreen"),
);

const QuotesListScreen = lazy(() => import("screens/private/admin-tool/quotes/QuotesListScreen"));
const QuotesCreateEditScreen = lazy(
  () => import("screens/private/admin-tool/quotes/QuotesCreateEditScreen"),
);
const QuotesDetailsScreen = lazy(
  () => import("screens/private/admin-tool/quotes/QuotesDetailsScreen"),
);
const QuotesUnauthorizedPreviewScreen = lazy(
  () => import("screens/private/admin-tool/quotes/QuotesUnauthorizedPreviewScreen"),
);
const QuotesCreateEditScreenNoBack = lazy(
  () => import("screens/private/admin-tool/quotes/QuotesCreateEditScreenNoBack"),
);

const EquipmentMakesListScreen = lazy(
  () => import("screens/private/admin-tool/equipment/equipmentMakes/EquipmentMakesListScreen"),
);

const EquipmentTypesListScreen = lazy(
  () => import("screens/private/admin-tool/equipment/equipmentTypes/EquipmentTypesListScreen"),
);

const EquipmentModelsListScreen = lazy(
  () => import("screens/private/admin-tool/equipment/equipmentModels/EquipmentModelsListScreen"),
);

// const EquipmentTypesAddEditScreen = lazy(
//   () => import("screens/private/admin-tool/equipment/equipmentTypes/EquipmentTypesAddEditScreen"),
// );

const WorkOrdersListScreen = lazy(
  () => import("screens/private/admin-tool/work-orders/WorkOrdersListScreen"),
);

const WorkOrdersDetailsScreen = lazy(
  () => import("screens/private/admin-tool/work-orders/WorkOrdersDetailsScreen"),
);

const MyProfileScreenScreen = lazy(
  () => import("screens/private/admin-tool/profile/MyProfileScreen"),
);

const PayrollScreen = lazy(() => import("screens/private/admin-tool/payroll/PayrollScreen"));

const SalesScreen = lazy(() => import("screens/private/admin-tool/sales/SalesScreen"));

const ItemsScreen = lazy(() => import("screens/private/admin-tool/items/ItemsScreen"));

const VendorsScreen = lazy(() => import("screens/private/admin-tool/vendors/VendorsListScreen"));

const VendorDetailsScreen = lazy(
  () => import("screens/private/admin-tool/vendors/VendorDetailsScreen"),
);

const ScheduleScreen = lazy(() => import("screens/private/admin-tool/ScheduleScreen"));
const SettingsScreen = lazy(() => import("screens/private/admin-tool/SettingsScreen"));
const NotificationDetailScreen = lazy(
  () => import("screens/private/admin-tool/settings/NotificationDetailScreen"),
);

// Public Screens
const ForgotPasswordScreen = lazy(() => import("screens/public/ForgotPasswordScreen"));
const SetPasswordScreen = lazy(() => import("screens/public/SetPasswordScreen"));
const SignInScreen = lazy(() => import("screens/public/SignInScreen"));
const ConfirmEmailScreen = lazy(() => import("screens/public/ConfirmEmailScreen"));
const QBConnectionCompleted = lazy(() => import("screens/public/QBConnectionCompleted"));
const AccessDeniedRoute = lazy(() => import("screens/common/AccessDeniedRoute"));

// Common Screens
const NotFoundRoute = lazy(() => import("screens/common/NotFoundRoute"));

const AppRoutes = () => {
  const { redirectByRole } = useAuth();
  const { currentUser } = useAppSelector((state) => state.app);
  const redirectPath = currentUser
    ? redirectByRole(currentUser?.roles || [], currentUser?.rolePermissions)
    : publicLinks.singin;

  return useRoutes([
    {
      element: <AuthLayout />,
      children: [
        {
          path: publicLinks.singin,
          element: currentUser ? <Navigate to={redirectPath} replace /> : <SignInScreen />,
        },
        { path: publicLinks.forgotPassword, element: <ForgotPasswordScreen /> },
        { path: publicLinks.setPassword, element: <SetPasswordScreen /> },
        {
          path: publicLinks.completeRegistration,
          element: <SetPasswordScreen isCompleteRegistration />,
        },
        { path: publicLinks.confirmEmail, element: <ConfirmEmailScreen /> },
        { path: publicLinks.qbConnection, element: <QBConnectionCompleted /> },
        { path: publicLinks.estimates, element: <QuotesUnauthorizedPreviewScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["User.Upsert"]]} />,
      path: "admin",
      children: [{ path: adminToolLinks.users, element: <UsersManagementScreen /> }],
    },
    {
      element: <ProtectedRoute />,
      children: [{ path: technichianToolLinks.myProfile, element: <MyProfileScreenScreen /> }],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Company.View"]]} />,
      children: [
        {
          path: technichianToolLinks.companyList,
          element: <CompanyListScreen />,
        },
        { path: technichianToolLinks.companyMap, element: <MapScreen /> },
        { path: technichianToolLinks.companiesDetails, element: <CompanyDetailsScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Vehicle.View"]]} />,
      children: [{ path: technichianToolLinks.vehiclesList, element: <VehiclesListScreen /> }],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Vehicle.Upsert"]]} />,
      children: [
        {
          path: technichianToolLinks.vehiclesCreate,
          element: <VehiclesAddEditScreen />,
        },
        {
          path: technichianToolLinks.vehiclesEdit,
          element: <VehiclesAddEditScreen />,
        },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Estimate.View"]]} />,
      children: [
        { path: technichianToolLinks.quotesList, element: <QuotesListScreen /> },
        { path: technichianToolLinks.quotesDetails, element: <QuotesDetailsScreen /> },
        {
          path: technichianToolLinks.quotesView,
          element: <QuotesCreateEditScreen />,
        },
      ],
    },
    {
      element: (
        <ProtectedRoute
          allowedPermissions={[
            UserPermissions["Estimate.Upsert"],
            UserPermissions["Item.View"],
            UserPermissions["Company.View"],
          ]}
          isStrict
        />
      ),
      children: [
        { path: technichianToolLinks.quotesCreate, element: <QuotesCreateEditScreen /> },
        {
          path: technichianToolLinks.quotesEdit,
          element: <QuotesCreateEditScreen />,
        },
        {
          path: technichianToolLinks.estimatesNew,
          element: <QuotesCreateEditScreenNoBack />,
        },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Calendar.View"]]} />,
      children: [{ path: technichianToolLinks.schedule, element: <ScheduleScreen /> }],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Company.Upsert"]]} />,
      children: [
        { path: technichianToolLinks.contactsCreate, element: <ContactsAddEditScreen /> },
        { path: technichianToolLinks.contactsEdit, element: <ContactsAddEditScreen /> },
      ],
    },
    {
      element: (
        <ProtectedRoute
          allowedPermissions={[
            UserPermissions["Estimate.View"],
            UserPermissions["Company.View"],
            UserPermissions["Item.View"],
          ]}
        />
      ),
      children: [
        {
          path: technichianToolLinks.companySales,
          element: <SalesScreen />,
        },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Company.View"]]} />,
      children: [
        { path: technichianToolLinks.equipmentCreate, element: <CompanyEquipmentAddEditScreen /> },
        { path: technichianToolLinks.equipmentEdit, element: <CompanyEquipmentEditTabScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Company.Upsert"]]} />,
      children: [
        { path: technichianToolLinks.notesCreate, element: <NotesAddEditScreen /> },
        { path: technichianToolLinks.notesEdit, element: <NotesAddEditScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Equipment.View"]]} />,
      children: [
        { path: technichianToolLinks.equipmentMakesList, element: <EquipmentMakesListScreen /> },
        { path: technichianToolLinks.equipmentTypesList, element: <EquipmentTypesListScreen /> },
        { path: technichianToolLinks.equipmentModelsList, element: <EquipmentModelsListScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={WORK_ORDER_VIEW_PERMISSIONS} />,
      children: [
        { path: technichianToolLinks.workOrdersList, element: <WorkOrdersListScreen /> },
        { path: technichianToolLinks.workOrderDetails, element: <WorkOrdersDetailsScreen /> },
        { path: technichianToolLinks.workOrdersNotesCreate, element: <NotesAddEditScreen /> },
        { path: technichianToolLinks.workOrdersNotesEdit, element: <NotesAddEditScreen /> },
      ],
    },
    {
      element: <ProtectedRoute />,
      children: [{ path: technichianToolLinks.payroll, element: <PayrollScreen /> }],
    },
    {
      element: (
        <ProtectedRoute
          allowedPermissions={[UserPermissions["Item.View"], UserPermissions["Vendor.View"]]}
        />
      ),
      children: [{ path: technichianToolLinks.items, element: <ItemsScreen /> }],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Vendor.View"]]} />,
      children: [
        { path: technichianToolLinks.vendors, element: <VendorsScreen /> },
        { path: technichianToolLinks.vendorsDetails, element: <VendorDetailsScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Settings.Update"]]} />,
      path: "admin",
      children: [
        { path: adminToolLinks.settings, element: <SettingsScreen /> },
        { path: adminToolLinks.notificationDetails, element: <NotificationDetailScreen /> },
      ],
    },
    {
      element: <ProtectedRoute allowedPermissions={[UserPermissions["Feature.Update"]]} />,
      path: "admin",
      children: [{ path: adminToolLinks.features, element: <FeaturesScreen /> }],
    },

    // host routes
    {
      element: <ProtectedRoute />,
      path: "host",
      children: [
        { path: hostToolLinks.tenants, element: <TenantsManagementScreen /> },
        { path: hostToolLinks.tenantDetails, element: <TenantDetailsScreen /> },
        { path: hostToolLinks.features, element: <FeaturesManagementScreen /> },
        { path: hostToolLinks.featureDetails, element: <FeatureDetailsScreen /> },
        { path: hostToolLinks.admins, element: <AdminManagementScreen /> },
        { path: hostToolLinks.roles, element: <RoleManagementScreen /> },
      ],
    },

    // common routes
    {
      element: <AccessDeniedRoute />,
      path: publicLinks.forbidden,
    },
    {
      element: <Navigate to={redirectPath} />,
      path: "/",
    },
    {
      element: <NotFoundRoute />,
      path: "*",
    },
  ]);
};

export default AppRoutes;
